import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";

import {
  Badge,
  Button,
  Card,
  Cluster,
  PageHeading,
  Stack,
  Table,
} from "~/components";
import { reportActionError } from "~/lib/actionErrors";
import { formatRecency } from "~/lib/dates";
import { queryClient } from "~/lib/queryClient";
import {
  notificationsListQueryOptions,
  unreadCount,
  useMarkNotificationRead,
  type Notification,
} from "~/lib/notifications";
import {
  myOpenTasksFilter,
  tasksListQueryKey,
  tasksListQueryOptions,
  type Task,
  type TaskState,
  type TasksCollection,
} from "~/lib/tasks";
import { useChangeTaskState } from "~/lib/events";
import { currentUserQueryOptions } from "~/lib/auth";

import * as css from "~/routes/_app.index.css";

// The home page is the user's queue — the work waiting on them is
// what they want to see when they land in the app, not a welcome
// card. The standalone /queue route was redundant; the sidebar's
// "Home" link is the single entry point.
//
// Reads from /api/tasks?user_id=me&state=todo&state=in+progress —
// the canonical tasks resource. The old /api/queue projection is gone.
//
// Beside the queue sits the notifications inbox (TMF-464).
export const Route = createFileRoute("/_app/")({
  component: HomePage,
  loader: () =>
    Promise.all([
      queryClient.ensureQueryData(tasksListQueryOptions(myOpenTasksFilter)),
      queryClient.ensureQueryData(notificationsListQueryOptions()),
    ]),
});

function HomePage() {
  // The current user is already in the cache (the layout loader
  // ensures it). Reading via suspense lets us pull the user's role
  // for the page subtitle without an extra fetch path.
  const { data: user } = useSuspenseQuery(currentUserQueryOptions);

  return (
    <Stack gap="lg">
      <PageHeading
        title={`Welcome, ${user.first_name}`}
        subtitle={subtitleFor(user.role)}
      />
      <div className={css.panels}>
        <TasksPanel role={user.role} />
        <NotificationsPanel />
      </div>
    </Stack>
  );
}

function TasksPanel({ role }: { role: string }) {
  const tasksQuery = useQuery(tasksListQueryOptions(myOpenTasksFilter));

  if (tasksQuery.isPending) {
    return (
      <Card>
        <p>Loading your tasks…</p>
      </Card>
    );
  }

  const tasks = tasksQuery.data?._embedded ?? [];

  if (tasks.length === 0) {
    return (
      <Card>
        <Stack gap="md" align="center" className={css.emptyState}>
          <p>{emptyStateMessage(role)}</p>
        </Stack>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <th>Task</th>
            <th>Case</th>
            <th>Role</th>
            <th>State</th>
            <th className={css.actionsCell} aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

// The notices the firm told this person about, newest first: the API's own
// order. A click marks the notice read and opens the board, which is where the
// work behind it lives (TMF-464 D11).
function NotificationsPanel() {
  const notificationsQuery = useQuery(notificationsListQueryOptions());
  const markRead = useMarkNotificationRead();
  const navigate = useNavigate();

  const notifications = notificationsQuery.data?._embedded ?? [];
  const unread = unreadCount(notifications);

  const heading = (
    <Cluster gap="sm" align="center">
      <h2 className={css.panelHeading}>Notifications</h2>
      {unread > 0 ? (
        <Badge variant="warning" data-testid="notifications-unread">
          {unread}
        </Badge>
      ) : null}
    </Cluster>
  );

  if (notificationsQuery.isPending) {
    return (
      <Card>
        <Stack gap="md">
          {heading}
          <p>Loading your notifications…</p>
        </Stack>
      </Card>
    );
  }

  const open = (notification: Notification) => {
    if (notification.read_at !== null) {
      navigate({ to: "/alpha" }).catch((error: unknown) => reportActionError(error));
      return;
    }
    markRead.mutate(notification.id, {
      onSuccess: () => {
        void navigate({ to: "/alpha" });
      },
    });
  };

  return (
    <Card>
      <Stack gap="md">
        {heading}
        {notifications.length === 0 ? (
          <Stack gap="md" align="center" className={css.emptyState}>
            <p>No notifications.</p>
          </Stack>
        ) : (
          <ul className={css.notificationList}>
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  data-testid="notification-row"
                  className={
                    notification.read_at === null
                      ? `${css.notificationRow} ${css.notificationUnread}`
                      : css.notificationRow
                  }
                  onClick={() => open(notification)}
                >
                  <span className={css.notificationBody} data-testid="notification-body">
                    {notification.body}
                  </span>
                  <time className={css.meta} dateTime={notification.created_at}>
                    {formatRecency(notification.created_at)}
                  </time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Stack>
    </Card>
  );
}

function TaskRow({ task }: { task: Task }) {
  const queryClient = useQueryClient();
  const mutation = useChangeTaskState();

  // Tasks are project-scoped now (TMF-170), and the label is the subtype's own
  // column (TMF-512): a matter names itself, a scanned drop names itself, and a
  // project whose subtype did not ride along is left with its id.
  const project = task._embedded.project;
  const caseId = project?._embedded?.case?.id;
  const caseName =
    project?._embedded?.case?.name
    ?? project?._embedded?.document_upload?.name
    ?? task.project_id;

  const isManual = task.is_manual;
  const inFlight = mutation.isPending
    && mutation.variables?.projectId === task.project_id
    && mutation.variables?.taskName === task.name;

  const handleMarkDone = () => {
    // Optimistic remove — the row vanishes immediately; rollback if
    // the server rejects. The mutation invalidates the tasks list on
    // success so the canonical state catches up.
    const queryKey = tasksListQueryKey(myOpenTasksFilter);
    const prev = queryClient.getQueryData<TasksCollection>(queryKey);
    queryClient.setQueryData<TasksCollection>(queryKey, (old) => {
      if (old === undefined) return old;
      return {
        ...old,
        _embedded: (old._embedded ?? []).filter((t) => t.id !== task.id),
      };
    });
    mutation.mutate(
      {
        projectId: task.project_id,
        // caseId scopes the cache invalidation; fall back to the project
        // id if (unexpectedly) no case is embedded.
        caseId: caseId ?? task.project_id,
        taskName: task.name,
        state: "complete",
      },
      {
        onError: (error) => {
          if (prev !== undefined) queryClient.setQueryData(queryKey, prev);
          // The row reappearing was the only sign of a rejection; say why
          // (TMF-362).
          reportActionError(error);
        },
      },
    );
  };

  return (
    <tr>
      <td className={css.taskName}>{task.name}</td>
      <td>
        {caseId !== undefined ? (
          <Link
            to="/cases/$id"
            params={{ id: caseId }}
            className={css.caseName}
          >
            {caseName}
          </Link>
        ) : (
          <span className={css.caseName}>{caseName}</span>
        )}
      </td>
      <td className={css.role}>{task.role}</td>
      <td>
        <Badge variant={badgeVariantFor(task.state)}>{task.state}</Badge>
      </td>
      <td className={css.actionsCell}>
        {task.state === "pending" ? (
          // Backend has accepted an event but the queue consumer
          // hasn't caught up — disable interaction until a refetch
          // reflects the real terminal state.
          <Button type="button" variant="secondary" disabled>
            Processing…
          </Button>
        ) : isManual ? (
          <Button
            type="button"
            variant="primary"
            onClick={handleMarkDone}
            disabled={inFlight}
          >
            {inFlight ? "Marking…" : "Mark done"}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function subtitleFor(role: string): string {
  if (role.length === 0) {
    return "No firm role assigned — your queue will stay empty until an admin sets one.";
  }
  return `Open tasks for you (${role}), across every case.`;
}

function emptyStateMessage(role: string): string {
  if (role.length === 0) {
    return "Your account has no firm role assigned, so the queue can't route tasks to you.";
  }
  return `No open ${role} tasks across your cases. Nice work.`;
}

// Map task state to a Badge variant. In-progress reads as "info"
// (the user's working it), to-do as "warning" (something to pick up).
// Pending also reads as "info" — the consumer hasn't caught up yet,
// the row is in flight. Anything else is neutral.
function badgeVariantFor(state: TaskState): "info" | "warning" | "neutral" {
  if (state === "in progress" || state === "pending") return "info";
  if (state === "todo") return "warning";
  return "neutral";
}
